/**
 * Manage backfill — the inline control after a leaver's name, and the dialog it opens.
 *
 * What renders comes entirely from the proxy-derived `BackfillState` (see
 * `proxy-server/lib/internalPlaygroundBackfills.js`):
 *
 *   - `open`           → a "Manage backfill" button. The dialog asks one question: backfill this
 *                        position, or not. Backfilling opens the propose form pre-filled; not
 *                        backfilling records the decision.
 *   - `not_backfilled` → a "Not backfilled" chip that opens the same dialog in its documenting
 *                        mode — why, and by whom. Only a decision somebody RECORDED can be
 *                        withdrawn here; a declined proposal or a departure with no backfill is
 *                        derived from Airtable and changes only there.
 *   - `backfilled`     → a quiet note, so a leaver without the button does not look forgotten.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import {
  type BackfillState,
  recordNoBackfill,
  withdrawNoBackfill,
} from '../../services/internalPlaygroundApi';
import { formatDay } from './backfillForm';
import { useViewerScope } from './viewerScope';

/** A leaver, as the control and the dialog need them. */
export interface BackfillPerson {
  employeeId: string;
  name: string;
  terminationDate: string | null;
  backfill: BackfillState;
}

const TIMELINE_QUERY_KEY = ['internal-playground-hiring-timeline'];

/** Sized like the `Badge`s elsewhere on these rows, so a row with a control still reads as one line. */
const INLINE_BUTTON = '!h-auto !min-h-0 shrink-0 !px-1.5 !py-0.5 !text-[10px] !font-medium';

/** Renders nothing for someone with no backfill state, or where the table was given no handler. */
export function BackfillControl({
  employeeId,
  name,
  terminationDate,
  backfill,
  onOpen,
}: {
  employeeId: string;
  name: string;
  terminationDate?: string | null;
  backfill?: BackfillState;
  onOpen?: (person: BackfillPerson) => void;
}) {
  const scope = useViewerScope();
  if (!backfill || !onOpen) return null;
  // A proposer decides backfills only for leavers in their own reporting line.
  if (!scope.inLine(employeeId)) return null;
  const person: BackfillPerson = { employeeId, name, terminationDate: terminationDate ?? null, backfill };
  if (backfill.state === 'backfilled') {
    return (
      <span className="shrink-0 text-[10px] text-zinc-400" title={backfill.title ?? undefined}>
        {backfill.via === 'hiring_plan' ? 'backfill in hiring plan' : 'backfill proposed'}
      </span>
    );
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={INLINE_BUTTON}
      onClick={(event: React.MouseEvent) => {
        // The control can sit inside a clickable row; opening the dialog must not also toggle it.
        event.stopPropagation();
        onOpen(person);
      }}
    >
      {backfill.state === 'open' ? 'Manage backfill' : 'Not backfilled'}
    </Button>
  );
}

function notBackfilledReason(person: BackfillPerson): string {
  const { backfill } = person;
  if (backfill.state !== 'not_backfilled') return '';
  if (backfill.reason === 'decided') {
    const who = backfill.decidedBy ?? 'someone';
    const when = backfill.decidedAt ? ` on ${formatDay(backfill.decidedAt.slice(0, 10))}` : '';
    return `Recorded as not backfilled by ${who}${when}.`;
  }
  if (backfill.reason === 'proposal_declined') {
    return 'The proposal to backfill this position was declined.';
  }
  return `Left${person.terminationDate ? ` on ${formatDay(person.terminationDate)}` : ''} with no backfill recorded in the Hiring Database.`;
}

export function ManageBackfillDialog({
  person,
  onClose,
  onBackfill,
}: {
  person: BackfillPerson;
  onClose: () => void;
  /** "I want to backfill this position" — the caller opens the pre-filled propose form. */
  onBackfill: (person: BackfillPerson) => void;
}) {
  const queryClient = useQueryClient();
  const done = () => {
    void queryClient.invalidateQueries({ queryKey: TIMELINE_QUERY_KEY });
    onClose();
  };
  const decline = useMutation({ mutationFn: () => recordNoBackfill(person.employeeId), onSuccess: done });
  const withdraw = useMutation({ mutationFn: () => withdrawNoBackfill(person.employeeId), onSuccess: done });
  const error = decline.error ?? withdraw.error;
  const { backfill } = person;
  const ends = person.terminationDate
    ? `${person.terminationDate < new Date().toISOString().slice(0, 10) ? 'Contract ended' : 'Contract ends'} on ${formatDay(person.terminationDate)}.`
    : '';

  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !w-[min(100vw-2rem,28rem)] gap-4 rounded p-6">
        <ModalHeader>
          <ModalTitle className="!text-xl !font-semibold">
            {backfill.state === 'open' ? `Manage backfill: ${person.name}` : `${person.name}: not backfilled`}
          </ModalTitle>
        </ModalHeader>

        {ends && <p className="text-sm text-zinc-500">{ends}</p>}

        {backfill.state === 'open' && (
          <div className="flex flex-col gap-2">
            <Button type="button" onClick={() => onBackfill(person)}>
              I want to backfill this position
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={decline.isPending}
              disabled={decline.isPending}
              onClick={() => {
                if (!decline.isPending) decline.mutate();
              }}
            >
              We will not backfill this position
            </Button>
          </div>
        )}

        {backfill.state === 'not_backfilled' && (
          <div className="space-y-3">
            <p className="text-sm text-zinc-700">{notBackfilledReason(person)}</p>
            {backfill.reason === 'decided' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                loading={withdraw.isPending}
                disabled={withdraw.isPending}
                onClick={() => {
                  if (!withdraw.isPending) withdraw.mutate();
                }}
              >
                Withdraw this decision
              </Button>
            ) : (
              <p className="text-xs text-zinc-500">
                This follows from Airtable and changes there, not here.
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600">{error instanceof Error ? error.message : 'Something went wrong.'}</p>
        )}

        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
